import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { useAdminAuth } from "./AuthContext";
import { useToast } from "../hooks/use-toast";

const ACTION_META = {
  confirm: {
    title: "Confirm booking",
    description: "Send a confirmation SMS to the customer and mark this booking as confirmed.",
    submitLabel: "Send & Confirm",
    submitClass: "bg-sky-600 hover:bg-sky-700 text-white",
    successMsg: "Booking confirmed",
  },
  decline: {
    title: "Decline booking",
    description: "Send a decline SMS to the customer and mark this booking as declined.",
    submitLabel: "Send & Decline",
    submitClass: "bg-rose-600 hover:bg-rose-700 text-white",
    successMsg: "Booking declined",
  },
  reschedule: {
    title: "Reschedule booking",
    description: "Pick a new date & time, edit the SMS, then send.",
    submitLabel: "Send & Reschedule",
    submitClass: "bg-amber-600 hover:bg-amber-700 text-white",
    successMsg: "Booking rescheduled",
  },
  complete: {
    title: "Mark job complete",
    description: "Send a thank-you SMS to the customer and mark this booking as completed.",
    submitLabel: "Send & Complete",
    submitClass: "bg-slate-700 hover:bg-slate-800 text-white",
    successMsg: "Job marked complete",
  },
};

const SMS_SEGMENT = 160;

export default function BookingActionModal({
  open,
  onOpenChange,
  action, // 'confirm' | 'decline' | 'reschedule'
  booking,
  onCompleted,
}) {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendSms, setSendSms] = useState(true);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [newTimeTo, setNewTimeTo] = useState("");
  const meta = ACTION_META[action] || {};

  // Load preview SMS whenever modal opens or date/time changes (for reschedule)
  useEffect(() => {
    if (!open || !booking || !action) return;
    setNewDate(booking.date || "");
    setNewTime(booking.time || "");
    setNewTimeTo(booking.time_to || "");
    setSendSms(true);
  }, [open, booking, action]);

  useEffect(() => {
    if (!open || !booking || !action) return;
    const load = async () => {
      setLoading(true);
      try {
        const params = action === "reschedule" && newDate && newTime
          ? { date: newDate, time: newTime, time_to: newTimeTo }
          : {};
        const r = await authAxios.get(`/bookings/${booking.id}/preview/${action}`, { params });
        setBody(r.data.body || "");
      } catch (e) {
        toast({ title: "Could not load SMS preview", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action, booking?.id, newDate, newTime, newTimeTo]);

  const submit = async () => {
    if (!booking || !action) return;
    setSubmitting(true);
    try {
      let r;
      if (action === "reschedule") {
        if (!newDate || !newTime) {
          toast({ title: "Pick a new date & time", variant: "destructive" });
          setSubmitting(false);
          return;
        }
        r = await authAxios.post(`/bookings/${booking.id}/reschedule`, {
          date: newDate,
          time: newTime,
          time_to: newTimeTo,
          body,
          send_sms: sendSms,
        });
      } else {
        r = await authAxios.post(`/bookings/${booking.id}/${action}`, {
          body,
          send_sms: sendSms,
        });
      }
      toast({ title: meta.successMsg });
      onCompleted?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Action failed",
        description: e?.response?.data?.detail || "Server error",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const charCount = body.length;
  const segments = Math.max(1, Math.ceil(charCount / SMS_SEGMENT));
  const overSegment = charCount > SMS_SEGMENT;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid={`booking-action-modal-${action}`}>
        <DialogHeader>
          <DialogTitle>{meta.title}</DialogTitle>
          <DialogDescription>{meta.description}</DialogDescription>
        </DialogHeader>
        {!booking ? null : (
          <div className="space-y-4">
            <div className="text-sm bg-slate-50 rounded-md p-3 border">
              <div className="font-semibold text-slate-800">{booking.name} · {booking.reference}</div>
              <div className="text-slate-600 text-xs">{booking.phone}</div>
              <div className="text-slate-600 text-xs">{booking.service_title}</div>
            </div>

            {action === "reschedule" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">New date</Label>
                  <input
                    type="date"
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                    data-testid="reschedule-date-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">New time (HH:MM)</Label>
                  <input
                    type="time"
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                    className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                    data-testid="reschedule-time-input"
                  />
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-slate-600">SMS to {booking.phone}</Label>
                <span className={`text-xs ${overSegment ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                  {charCount} chars · {segments} segment{segments > 1 ? "s" : ""}
                </span>
              </div>
              {loading ? (
                <div className="text-xs text-slate-500 py-2 flex items-center gap-2">
                  <Loader2 className="animate-spin" size={14} /> Generating preview…
                </div>
              ) : (
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={6}
                  data-testid="action-sms-body"
                  className="text-sm"
                />
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={sendSms}
                onChange={(e) => setSendSms(e.target.checked)}
                data-testid="action-send-sms-toggle"
              />
              Send SMS to customer (uncheck to update status silently)
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="action-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || loading}
                className={meta.submitClass}
                data-testid="action-submit-btn"
              >
                {submitting && <Loader2 className="animate-spin mr-2" size={14} />}
                {sendSms ? meta.submitLabel : "Update status only"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
